/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select'
import { Button, Form, FormGroup, FormText, Input, Label } from 'reactstrap';
import { loadCommon } from '../../actions/common';
import { getCountryOptions } from '../../selectors/common';
import { BaseStepCompProps } from './types';
import { useAppDispatch } from '../../reducers';
import { MonthDayPicker } from './MonthDayPicker';

interface CompanyInfoProps extends Omit<BaseStepCompProps, 'handlePrevious'> {
}

const CompanyInfo = (props: CompanyInfoProps) => {
  const { updateForm, formData } = props;
  const dispatch = useAppDispatch();
  const countryList = useSelector(getCountryOptions);
  const topCountries = [{ label: 'United Kingdom of Great Britain and Northern Ireland', value: 'GB' }, { value: 'US', label: 'United States of America' }];
  const filteredCountryCodes = topCountries.map(c => c.value);
  const filteredTopCountries = countryList.filter(c => !filteredCountryCodes.includes(c.value));
  const countries = [...topCountries, ...filteredTopCountries]

  const addressData = formData.address;

  const handleAddressInput = (name: string, value: any) => {
    updateForm('address', {
      ...formData['address'],
      [name]: value
    });
  }

  useEffect(() => {
    dispatch(loadCommon());
  }, [dispatch])

  const isFormValid = formData.initiativeCountryCode && formData.initiativeName && addressData?.line1 && addressData.postcode;

  const handleNext = (e: React.ChangeEvent<any>) => {
    e.preventDefault();
    if (isFormValid) {
      props.handleNext()
    }
  };

  return <>
    <div className='d-lg-flex justify-content-between onboarding-form'>
      <aside className='title-container'>
        <div className='h1'>Welcome!</div>
        <div className='h1 mb-3'>Let's get your account set up</div>
        <p>This only takes a minute.</p>
      </aside>
      <section className='form-container'>
        <Form className='mt-5' >
          <div className='row mb-3'>
            <div className='col'>
              <FormGroup>
                <Label for='initiativeName'>What is your company's name?</Label>
                <Input
                  id='initiativeName'
                  name='initiativeName'
                  type='text'
                  placeholder='Company name'
                  onChange={(e) => updateForm('initiativeName', e.target.value)}
                  value={formData.initiativeName}
                  required
                />
              </FormGroup>
            </div>
          </div>
          <div className='row mb-3'>
            <div className='col'>
              <FormGroup>
                <Label for='initiativeCountryCode'>Where is the headquarters?</Label>
                <Select
                  placeholder={'Select country'}
                  options={countries}
                  value={countries.find(c => c.value === formData.initiativeCountryCode) ?? null}
                  onChange={(option) => updateForm('initiativeCountryCode', option?.value ?? '')}
                />
              </FormGroup>
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <FormGroup className='mb-1'>
                <Label for='line1'>What is the registered address?</Label>
                <Input
                  id='line1'
                  name='line1'
                  type='text'
                  placeholder='Address Line 1'
                  onChange={(e) => handleAddressInput('line1', e.target.value)}
                  value={addressData?.line1 ?? ''}
                  required
                />
              </FormGroup>
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <FormGroup className='mb-1'>
                <Input
                  id='line2'
                  name='line2'
                  type='text'
                  placeholder='Address Line 2'
                  onChange={(e) => handleAddressInput('line2', e.target.value)}
                  value={addressData?.line2 ?? ''}
                  required
                />
              </FormGroup>
            </div>
          </div>
          <div className='row'>
            <div className='col'>
              <FormGroup className='mb-1'>
                <Input
                  id='city'
                  name='city'
                  type='text'
                  placeholder='City'
                  onChange={(e) => handleAddressInput('city', e.target.value)}
                  value={addressData?.city ?? ''}
                  required
                />
              </FormGroup>
            </div>
          </div>
          <div className='row mb-3'>
            <div className='col'>
              <FormGroup>
                <Input
                  id='postcode'
                  name='postcode'
                  type='text'
                  placeholder='Postal code'
                  onChange={(e) => handleAddressInput('postcode', e.target.value)}
                  value={addressData?.postcode ?? ''}
                  required
                />
              </FormGroup>
            </div>
          </div>
          <div className='row mb-3'>
            <div className='col'>
              <FormGroup>
                <Label for='registrationNumber'>Company registration number</Label>
                <Input
                  placeholder='Registration number'
                  id='registrationNumber'
                  name='registrationNumber'
                  type='text'
                  onChange={(e) => updateForm('registrationNumber', e.target.value)}
                  value={formData.registrationNumber}
                />
                <FormText>Optional</FormText>
              </FormGroup>
            </div>
          </div>
          <div className='row mb-3'>
            <div className='col'>
              <FormGroup>
                <Label for='financialEndDate'>
                  What is the end date of your annual financial year?
                </Label>
                <MonthDayPicker value={formData.financialEndDate} onChange={(value) => updateForm('financialEndDate', value)} />
                <FormText>Optional</FormText>
              </FormGroup>
            </div>
          </div>
        </Form>
        <div className='text-right'>
          <Button color='primary' disabled={!isFormValid} className='ml-1' onClick={handleNext}>Next<i className='far fa-angle-right ml-2' /></Button>
        </div>
      </section>
    </div>
  </>
}

export default CompanyInfo;
